<template>
  <section id="section-deposit" class="wallet-section wallet-section--deposit">
    <header class="wallet-section__header">
      <h2>{{ $t('wallet.depositTitle') }}</h2>
      <p>{{ depositSubtitle }}</p>
    </header>

    <template v-if="isPgMode">
      <article class="wallet-guide">
        <h3 class="wallet-guide__title">{{ $t('wallet.pgDepositGuideTitle') }}</h3>
        <div class="wallet-guide__body">
          <p>{{ $t('wallet.pgDepositGuideBody') }}</p>
        </div>
      </article>
      <article class="wallet-panel">
        <h3 class="wallet-panel__title">{{ $t('wallet.pgDepositRequestTitle') }}</h3>
        <p v-if="pgSuccessMessage" class="wallet-panel__success">{{ pgSuccessMessage }}</p>
        <p v-if="pgSubmitError" class="wallet-panel__error">{{ pgSubmitError }}</p>
        <p v-if="pgCashierUrl" class="wallet-panel__success">
          <a :href="pgCashierUrl" target="_blank" rel="noopener noreferrer">{{ $t('wallet.pgDepositOpenCashier') }}</a>
        </p>
        <form class="wallet-form" @submit.prevent="submitPgDeposit">
          <div class="wallet-form__field">
            <span class="wallet-form__label">{{ $t('wallet.pgWayCodeLabel') }} <em>({{ $t('wallet.required') }})</em></span>
            <div class="wallet-form__way-options" role="radiogroup" :aria-label="$t('wallet.pgWayCodeLabel')">
              <button
                v-for="option in pgWayOptions"
                :key="option.code"
                type="button"
                role="radio"
                class="wallet-form__way-option"
                :class="{ 'wallet-form__way-option--active': pgWayCode === option.code }"
                :aria-checked="pgWayCode === option.code"
                :disabled="pgSubmitting"
                @click="selectPgWayCode(option.code)"
              >{{ $t(option.labelKey) }}</button>
            </div>
          </div>
          <div class="wallet-form__field wallet-form__field--amount">
            <span class="wallet-form__label">{{ $t('wallet.depositAmountLabel') }} <em>({{ $t('wallet.required') }})</em></span>
            <p class="wallet-form__amount-selected">
              {{ pgAmount ? formatCurrency(pgAmount) : $t('wallet.pgAmountUnselected') }}
            </p>
          </div>
          <div class="wallet-form__amount-options" role="group" :aria-label="$t('wallet.pgAmountSelectLabel')">
            <button
              v-for="option in pgAmountOptions"
              :key="option.amount"
              type="button"
              class="wallet-form__amount-option"
              :class="{ 'wallet-form__amount-option--active': pgAmount === option.amount }"
              :aria-pressed="pgAmount === option.amount"
              :disabled="pgSubmitting || !pgWayCode"
              @click="selectPgAmount(option.amount)"
            >{{ option.label }}</button>
          </div>
          <div class="wallet-form__actions">
            <button type="submit" class="wallet-form__submit" :disabled="pgSubmitting || !pgWayCode || !pgAmount">
              {{ pgSubmitting ? $t('wallet.pgDepositRedirecting') : $t('wallet.depositRequest') }}
            </button>
          </div>
        </form>
      </article>
    </template>

    <nav
      v-if="!isPgMode && showWalletTabs"
      class="wallet-tabs"
      role="tablist"
      :aria-label="$t('wallet.depositTabAria')"
    >
      <button
        v-if="showBankWalletTab"
        type="button"
        role="tab"
        class="wallet-tabs__btn"
        :class="{ 'wallet-tabs__btn--active': depositTab === 'bank' }"
        :aria-selected="depositTab === 'bank'"
        @click="setDepositTab('bank')"
      >
        <span class="wallet-tabs__icon" aria-hidden="true">{{ siteCurrency }}</span>
        {{ $t('wallet.depositTabBank') }}
      </button>
      <button
        v-if="showTetherWalletTab"
        type="button"
        role="tab"
        class="wallet-tabs__btn"
        :class="{ 'wallet-tabs__btn--active': depositTab === 'tether' }"
        :aria-selected="depositTab === 'tether'"
        @click="setDepositTab('tether')"
      >
        <span class="wallet-tabs__icon" aria-hidden="true">₮</span>
        {{ $t('wallet.depositTabTether') }}
      </button>
    </nav>

    <article v-show="!isPgMode && depositTab === 'tether'" class="wallet-guide">
      <h3 class="wallet-guide__title">{{ $t('wallet.tetherDepositGuideTitle') }}</h3>
      <div class="wallet-guide__body">
        <p>
          {{ $t('wallet.tetherDepositGuideParagraph1') }}
        </p>
        <p>
          {{ $t('wallet.tetherDepositGuideParagraph2') }}
        </p>
        <p>
          {{ $t('wallet.tetherDepositGuideParagraph3Prefix') }}
          <button type="button" class="wallet-guide__link" @click="$emit('telegram')">
            {{ $t('nav.telegramInquiry') }}
          </button>
          {{ $t('wallet.tetherDepositGuideParagraph3Suffix') }}
        </p>
      </div>
    </article>

    <article v-show="!isPgMode && depositTab === 'bank'" class="wallet-guide">
      <h3 class="wallet-guide__title">{{ $t('wallet.bankDepositGuideTitle') }}</h3>
      <div class="wallet-guide__body">
        <p>
          {{ $t('wallet.bankDepositGuideParagraph1') }}
        </p>
        <p>
          {{ $t('wallet.bankDepositGuideParagraph2') }}
        </p>
        <p>
          {{ $t('wallet.bankDepositGuideParagraph3') }}
        </p>
      </div>
    </article>

    <!-- Tether deposit -->
    <article v-show="!isPgMode && depositTab === 'tether'" class="wallet-panel">
      <h3 class="wallet-panel__title">{{ $t('wallet.tetherDepositRequestTitle') }}</h3>

      <p v-if="successMessage" class="wallet-panel__success">{{ successMessage }}</p>
      <div v-if="successTetherAddress" class="wallet-panel__bankinfo wallet-panel__bankinfo--tether">
        <p class="wallet-panel__bankinfo-label">
          {{ $t('wallet.tetherAddressGuide') }}
          <span class="wallet-panel__bankinfo-network">TRC20</span>
        </p>
        <p class="wallet-panel__bankinfo-network-hint">{{ $t('wallet.networkHint') }}</p>
        <p v-if="requestedUsdtAmount" class="wallet-panel__bankinfo-usdt">
          {{ $t('wallet.requestedAmount', { amount: requestedUsdtAmount }) }}
        </p>
        <div v-if="tetherQrUrl || tetherQrLoading" class="wallet-panel__bankinfo-qr">
          <img
            v-if="tetherQrUrl"
            :src="tetherQrUrl"
            :alt="$t('wallet.qrAlt')"
            class="wallet-panel__bankinfo-qr-image"
            width="220"
            height="220"
          >
          <p v-else class="wallet-panel__bankinfo-qr-loading">{{ $t('wallet.qrLoading') }}</p>
          <p v-if="tetherQrUrl" class="wallet-panel__bankinfo-qr-caption">{{ $t('wallet.qrCaption') }}</p>
        </div>
        <div class="wallet-panel__bankinfo-address-row">
          <p class="wallet-panel__bankinfo-address">{{ successTetherAddress }}</p>
          <button
            type="button"
            class="wallet-panel__bankinfo-copy"
            :class="{ 'wallet-panel__bankinfo-copy--done': addressCopied }"
            :title="addressCopied ? $t('wallet.copiedAddress') : $t('wallet.copyAddress')"
            :aria-label="addressCopied ? $t('wallet.addressCopied') : $t('wallet.copyAddress')"
            @click="copyTetherAddress"
          >
            <svg
              class="wallet-panel__bankinfo-copy-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                v-if="addressCopied"
                d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2Z"
              />
              <template v-else>
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1Zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2Zm0 16H8V7h11v14Z" />
              </template>
            </svg>
          </button>
        </div>
      </div>
      <p v-if="submitError" class="wallet-panel__error">{{ submitError }}</p>

      <form class="wallet-form" @submit.prevent="submitDeposit">
        <div class="wallet-form__field wallet-form__field--amount">
          <span class="wallet-form__label">{{ $t('wallet.depositAmountLabel') }} <em>({{ $t('wallet.required') }})</em></span>
          <WalletUsdtQuote
            mode="deposit"
            :krw-amount="amount"
            @quote-change="onUsdtQuoteChange"
          >
            <input
              v-model="amountDisplay"
              type="text"
              inputmode="numeric"
              class="wallet-form__amount-input"
              :placeholder="amountPlaceholder"
              autocomplete="off"
              @input="onAmountInput"
            >
          </WalletUsdtQuote>
        </div>

        <WalletQuickAmountButtons @add="addAmount" @reset="resetAmount" />

        <div class="wallet-form__field wallet-form__field--wallet-check">
          <span class="wallet-form__label">{{ $t('wallet.walletAddressLabel') }} <em>({{ $t('wallet.required') }})</em></span>
          <div class="wallet-wallet-check">
            <input
              v-model.trim="account"
              type="text"
              maxlength="64"
              class="wallet-wallet-check__input"
              :placeholder="$t('wallet.walletAddressPlaceholder')"
              autocomplete="off"
              :disabled="walletCheckLoading"
              @input="onWalletAddressInput"
            >
            <button
              type="button"
              class="wallet-wallet-check__btn"
              :disabled="walletCheckLoading || !account"
              @click="checkWalletBalance"
            >
              {{ walletCheckLoading ? $t('wallet.checking') : $t('wallet.walletCheck') }}
            </button>
            <span
              v-if="walletCheckVerified"
              class="wallet-wallet-check__balance"
            >
              <span class="wallet-wallet-check__balance-label">{{ $t('wallet.walletBalanceLabel') }}</span>
              <strong class="wallet-wallet-check__balance-value">{{ walletUsdtBalanceDisplay }}</strong>
              <span class="wallet-wallet-check__balance-unit">USDT</span>
            </span>
          </div>
          <p v-if="walletCheckError" class="wallet-wallet-check__error" role="alert">{{ walletCheckError }}</p>
          <p v-else-if="!walletCheckVerified" class="wallet-wallet-check__hint">
            {{ $t('wallet.walletCheckHintDeposit') }}
          </p>
        </div>

        <div class="wallet-form__actions">
          <button
            type="submit"
            class="wallet-form__submit"
            :disabled="submitting || !walletCheckVerified"
          >
            {{ submitting ? $t('wallet.requesting') : $t('wallet.depositRequest') }}
          </button>
          <button type="button" class="wallet-form__ghost" :disabled="submitting" @click="$emit('support')">
            {{ $t('wallet.supportInquiry') }}
          </button>
        </div>
      </form>
    </article>

    <!-- Bank deposit -->
    <article v-show="!isPgMode && depositTab === 'bank'" class="wallet-panel">
      <h3 class="wallet-panel__title">{{ $t('wallet.bankDepositRequestTitle') }}</h3>

      <p v-if="bankSuccessMessage" class="wallet-panel__success">{{ bankSuccessMessage }}</p>

      <div v-if="hasSuccessBankDetails" class="wallet-panel__bankinfo wallet-panel__bankinfo--account">
        <p class="wallet-panel__bankinfo-label">{{ $t('wallet.bankAccountGuide') }}</p>
        <p class="wallet-panel__bankinfo-hint">
          {{ $t('wallet.bankDepositHint', { amount: formatCurrency(amount) }) }}
        </p>

        <dl class="wallet-bank-card">
          <div v-if="successBankDetails.bankName" class="wallet-bank-card__row">
            <dt>{{ $t('wallet.bankName') }}</dt>
            <dd>{{ successBankDetails.bankName }}</dd>
            <button
              type="button"
              class="wallet-panel__bankinfo-copy"
              :class="{ 'wallet-panel__bankinfo-copy--done': bankCopyState.bankName }"
              :aria-label="$t('wallet.copyBankName')"
              @click="copyBankField('bankName', successBankDetails.bankName)"
            >
              <svg class="wallet-panel__bankinfo-copy-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1Zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2Zm0 16H8V7h11v14Z" />
              </svg>
            </button>
          </div>
          <div v-if="successBankDetails.bankAccount" class="wallet-bank-card__row">
            <dt>{{ $t('wallet.accountNumber') }}</dt>
            <dd>{{ successBankDetails.bankAccount }}</dd>
            <button
              type="button"
              class="wallet-panel__bankinfo-copy"
              :class="{ 'wallet-panel__bankinfo-copy--done': bankCopyState.accountNo }"
              :aria-label="$t('wallet.copyAccountNo')"
              @click="copyBankField('accountNo', successBankDetails.bankAccount)"
            >
              <svg class="wallet-panel__bankinfo-copy-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1Zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2Zm0 16H8V7h11v14Z" />
              </svg>
            </button>
          </div>
          <div v-if="successBankDetails.bankMaster" class="wallet-bank-card__row">
            <dt>{{ $t('wallet.accountHolder') }}</dt>
            <dd>{{ successBankDetails.bankMaster }}</dd>
            <button
              type="button"
              class="wallet-panel__bankinfo-copy"
              :class="{ 'wallet-panel__bankinfo-copy--done': bankCopyState.holder }"
              :aria-label="$t('wallet.copyHolder')"
              @click="copyBankField('holder', successBankDetails.bankMaster)"
            >
              <svg class="wallet-panel__bankinfo-copy-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1Zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2Zm0 16H8V7h11v14Z" />
              </svg>
            </button>
          </div>
        </dl>
      </div>

      <p v-if="bankSubmitError" class="wallet-panel__error">{{ bankSubmitError }}</p>

      <form class="wallet-form" @submit.prevent="submitBankDeposit">
        <div class="wallet-form__field wallet-form__field--amount">
          <span class="wallet-form__label">{{ $t('wallet.depositAmountLabel') }} <em>({{ $t('wallet.required') }})</em></span>
          <input
            v-model="amountDisplay"
            type="text"
            inputmode="numeric"
            class="wallet-form__amount-input"
            :placeholder="amountPlaceholder"
            autocomplete="off"
            @input="onAmountInput"
          >
        </div>

        <WalletQuickAmountButtons @add="addAmount" @reset="resetAmount" />

        <div class="wallet-form__group">
          <span class="wallet-form__label">{{ $t('wallet.bankInfoGroup') }} <em>({{ $t('wallet.required') }})</em></span>

          <label class="wallet-form__field">
            <span class="wallet-form__sublabel">{{ $t('wallet.bankNameLabel') }}</span>
            <input
              v-model.trim="bankInputName"
              type="text"
              maxlength="40"
              :placeholder="$t('wallet.bankNamePlaceholder')"
              autocomplete="off"
            >
          </label>

          <label class="wallet-form__field">
            <span class="wallet-form__sublabel">{{ $t('wallet.depositorNameLabel') }}</span>
            <input
              v-model.trim="bankInputMaster"
              type="text"
              maxlength="40"
              :placeholder="$t('wallet.depositorNamePlaceholder')"
              autocomplete="name"
            >
          </label>

          <label class="wallet-form__field">
            <span class="wallet-form__sublabel">{{ $t('wallet.accountNumberLabel') }}</span>
            <input
              v-model.trim="bankInputAccount"
              type="text"
              inputmode="numeric"
              maxlength="30"
              :placeholder="$t('wallet.accountNumberPlaceholder')"
              autocomplete="off"
            >
          </label>
        </div>

        <ul class="wallet-form__notes">
          <li>{{ $t('wallet.depositNoteMismatch') }}</li>
        </ul>

        <div class="wallet-form__actions">
          <button type="submit" class="wallet-form__submit" :disabled="bankSubmitting">
            {{ bankSubmitting ? $t('wallet.requesting') : $t('wallet.depositRequest') }}
          </button>
          <button type="button" class="wallet-form__ghost" :disabled="bankSubmitting" @click="$emit('support')">
            {{ $t('wallet.supportInquiry') }}
          </button>
        </div>
      </form>
    </article>

    <article class="wallet-panel wallet-panel--history">
      <div class="wallet-panel__title-row">
        <h3 class="wallet-panel__title">{{ $t('wallet.depositHistoryTitle') }}</h3>
        <button
          type="button"
          class="wallet-panel__title-action"
          :disabled="historyLoading"
          :aria-label="$t('wallet.historyReloadDeposit')"
          @click="reloadHistory"
        >
          <svg
            class="wallet-panel__title-action-icon"
            viewBox="0 0 24 24"
            width="16"
            height="16"
            aria-hidden="true"
          >
            <path
              d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5M4 12a8 8 0 1 1 2.34 5.66M4 20v-5h5"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
      <p class="wallet-panel__desc">{{ $t('wallet.historyDesc') }}</p>

      <p v-if="historyLoading" class="wallet-section__status">{{ $t('wallet.loadingDepositHistory') }}</p>
      <p v-else-if="historyError" class="wallet-section__status wallet-section__status--error">{{ historyError }}</p>
      <p v-else-if="!historyRows.length" class="wallet-section__status">{{ $t('wallet.emptyDepositHistory') }}</p>

      <div v-else class="wallet-table-wrap">
        <table class="wallet-table">
          <thead>
            <tr>
              <th scope="col">{{ $t('wallet.colRequestDate') }}</th>
              <th scope="col">{{ $t('wallet.colType') }}</th>
              <th scope="col" class="wallet-table__num">{{ $t('wallet.colAmount') }}</th>
              <th scope="col">{{ $t('wallet.colDepositInfo') }}</th>
              <th scope="col">{{ $t('wallet.colMemo') }}</th>
              <th scope="col">{{ $t('wallet.colStatus') }}</th>
              <th scope="col">{{ $t('wallet.colProcessedDate') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in historyRows" :key="row.idx">
              <td>{{ formatUtcDateTime(row.regdate) }}</td>
              <td>
                <span class="wallet-type" :class="chargeTypeClass(row.type)">
                  {{ chargeTypeLabel(row.type) }}
                </span>
              </td>
              <td class="wallet-table__num">{{ formatCurrency(row.amount) }}</td>
              <td class="wallet-table__account" :title="formatChargeAccountInfoTitle(row)">
                {{ formatChargeAccountInfo(row) }}
              </td>
              <td class="wallet-table__memo">
                <charge-memo-cell :memo="row.memo" />
              </td>
              <td>
                <span class="wallet-status" :class="chargeStatusClass(row.status)">
                  {{ chargeStatusLabel(row.status) }}
                </span>
              </td>
              <td>{{ formatChargeProcDate(row) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <footer v-if="historyTotal > 0" class="wallet-pagination">
        <button
          type="button"
          class="wallet-pagination__btn"
          :disabled="historyPage <= 1 || historyLoading"
          @click="goHistoryPage(historyPage - 1)"
        >
          {{ $t('common.prev') }}
        </button>
        <span class="wallet-pagination__info">{{ historyPage }} / {{ historyTotalPages }}</span>
        <button
          type="button"
          class="wallet-pagination__btn"
          :disabled="historyPage >= historyTotalPages || historyLoading"
          @click="goHistoryPage(historyPage + 1)"
        >
          {{ $t('common.next') }}
        </button>
      </footer>
    </article>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import { copyToClipboard } from '@/utils/clipboard'
import { showToast } from '@/utils/toast'
import { generateQrDataUrl } from '@/utils/qrCode'
import WalletUsdtQuote from '@/components/black-gold/WalletUsdtQuote.vue'
import WalletQuickAmountButtons from '@/components/black-gold/WalletQuickAmountButtons.vue'
import ChargeMemoCell from '@/components/black-gold/ChargeMemoCell.vue'
import { WalletService } from '@/services'
import { getLoginId } from '@/utils/auth'
import { getCookie } from '@/utils/cookie'
import { formatMoney, formatIntegerInput, formatUtcDateTime, displayValue, convertFiatToUsdt, formatUsdtAmount, formatCurrencyAmount } from '@/utils/format'
import { subscribeUsdtFiatRate } from '@/services/usdtRate'
import { resolveSiteCurrency } from '@/utils/siteBrand'
import store from '@/store'
import {
  CHARGE_LIST_PAGE_SIZE,
  getMinChargeAmount,
  getAmountPlaceholder,
  getPgDepositAmounts,
  getPgMinDepositAmount,
  formatPgAmountLabel,
  normalizeChargeList,
  chargeStatusLabel,
  chargeStatusClass,
  chargeTypeLabel,
  chargeTypeClass,
  formatChargeProcDate,
  buildChargeRequestMemo,
  buildTetherChargeAccount,
  formatTetherAccountInfo,
  formatTetherAccountInfoTitle,
  buildBankChargeAccount,
  extractTetherFromChargeData,
  extractBankFromChargeData
} from '@/utils/wallet'
import {
  resolvePaymentMethod,
  paymentMethodAllowsBank,
  paymentMethodAllowsTether,
  normalizeWalletTab,
  canSelectWalletTab,
  getDepositSubtitle,
  isPgPaymentMethod,
  PG_DEPOSIT_WAY_OPTIONS,
  normalizePgDepositWayCode,
  resolveDefaultPgUserWayCode,
  toPgApiWayCode
} from '@/utils/paymentMethod'
import {
  fetchTrc20UsdtBalance,
  formatTrc20UsdtBalance,
  isValidTronAddress
} from '@/utils/tronUsdt'

const CHARGE_REQUEST_TOOMANY_CODE = 2026
const CHARGE_MONEY_MIN_CODE = 2055
const PG_AMOUNT_NOT_ALLOWED_CODE = 2056

/** @returns {{ key: string, params?: object }} */
function resolveChargeRequestError (body) {
  const fail = { key: 'wallet.depositFail' }
  if (!body) return fail
  const code = Number(body.code)
  const message = String(body.message || '').toUpperCase()
  if (code === CHARGE_REQUEST_TOOMANY_CODE || message === 'CHARGE_REQUEST_TOOMANY') {
    return { key: 'wallet.depositPending' }
  }
  if (code === CHARGE_MONEY_MIN_CODE || message === 'CHARGE_MONEY_MIN') {
    const minAmount = isPgPaymentMethod(store.state.siteInfo)
      ? getPgMinDepositAmount('cashapp')
      : getMinChargeAmount()
    return {
      key: 'wallet.depositMinAmount',
      params: { amount: formatCurrencyAmount(minAmount, resolveSiteCurrency(store.state.siteInfo)) }
    }
  }
  if (code === PG_AMOUNT_NOT_ALLOWED_CODE || message === 'PG_AMOUNT_NOT_ALLOWED') {
    return { key: 'wallet.pgAmountRequired' }
  }
  if (code === 2054 || message === 'PG_WAYCODE_REQUIRED') {
    return { key: 'wallet.pgWayCodeRequired' }
  }
  if (code === 2052 || message === 'PG_NOT_CONFIGURED') return fail
  if (code === 2053 || message === 'PG_CREATE_FAILED') return fail
  return fail
}

export default {
  name: 'BlackGoldDepositPage',
  components: { WalletUsdtQuote, WalletQuickAmountButtons, ChargeMemoCell },
  data () {
    return {
      depositTab: 'bank',
      amount: 0,
      amountDisplay: '',
      account: '',
      bankInputName: '',
      bankInputMaster: '',
      bankInputAccount: '',
      submitting: false,
      submitError: '',
      successMessage: '',
      successTetherAddress: '',
      tetherQrUrl: '',
      tetherQrLoading: false,
      addressCopied: false,
      addressCopiedTimer: null,
      bankSubmitting: false,
      bankSubmitError: '',
      bankSuccessMessage: '',
      successBankDetails: {
        bankName: '',
        bankAccount: '',
        bankMaster: ''
      },
      bankCopyState: {
        bankName: false,
        accountNo: false,
        holder: false
      },
      bankCopyTimers: {},
      historyLoading: false,
      historyError: '',
      historyRows: [],
      historyTotal: 0,
      historyPage: 1,
      usdtRate: null,
      walletCheckLoading: false,
      walletCheckVerified: false,
      walletUsdtBalance: null,
      walletCheckError: '',
      usdtQuote: {
        baseUsdt: null,
        totalUsdt: null
      },
      pgSubmitting: false,
      pgSubmitError: '',
      pgSuccessMessage: '',
      pgCashierUrl: '',
      pgAmount: 0,
      pgWayCode: 'cashapp'
    }
  },
  computed: {
    ...mapState({
      siteInfo: state => state.siteInfo
    }),
    paymentMethod () {
      return resolvePaymentMethod(this.siteInfo)
    },
    isPgMode () {
      return isPgPaymentMethod(this.paymentMethod)
    },
    pgWayOptions () {
      return PG_DEPOSIT_WAY_OPTIONS
    },
    pgAmountOptions () {
      return getPgDepositAmounts(this.pgWayCode).map((amount) => ({
        amount,
        label: formatPgAmountLabel(amount)
      }))
    },
    showBankWalletTab () {
      return !this.isPgMode && paymentMethodAllowsBank(this.paymentMethod)
    },
    showTetherWalletTab () {
      return !this.isPgMode && paymentMethodAllowsTether(this.paymentMethod)
    },
    showWalletTabs () {
      return this.showBankWalletTab && this.showTetherWalletTab
    },
    depositSubtitle () {
      return getDepositSubtitle(this.paymentMethod)
    },
    historyPageSize () {
      return CHARGE_LIST_PAGE_SIZE
    },
    historyTotalPages () {
      return Math.max(1, Math.ceil(this.historyTotal / this.historyPageSize))
    },
    requestedUsdtAmount () {
      if (this.usdtQuote.totalUsdt != null) {
        return formatUsdtAmount(this.usdtQuote.totalUsdt, { maxDecimals: 2, minDecimals: 2 })
      }
      const usdt = convertFiatToUsdt(this.amount, this.usdtRate)
      if (usdt == null) return ''
      return formatUsdtAmount(usdt, { maxDecimals: 2, minDecimals: 2 })
    },
    hasSuccessBankDetails () {
      const d = this.successBankDetails
      return !!(d.bankName || d.bankAccount || d.bankMaster)
    },
    walletUsdtBalanceDisplay () {
      if (!this.walletCheckVerified || this.walletUsdtBalance == null) return '-'
      return formatTrc20UsdtBalance(this.walletUsdtBalance)
    },
    amountPlaceholder () {
      return getAmountPlaceholder()
    },
    minChargeAmount () {
      return getMinChargeAmount()
    }
  },
  watch: {
    siteInfo: {
      immediate: true,
      handler () {
        this.syncDepositTabWithPaymentMethod()
        this.syncPgWayCodeFromSite()
        this.syncPgAmountWithWayCode()
        this.resubscribeUsdtRate()
      }
    },
    pgWayCode () {
      this.syncPgAmountWithWayCode()
    }
  },
  mounted () {
    this.syncDepositTabWithPaymentMethod()
    this.prefillBankInputFromCookies()
    this.loadChargeHistory()
  },
  beforeDestroy () {
    if (this.addressCopiedTimer) {
      window.clearTimeout(this.addressCopiedTimer)
    }
    this.clearBankCopyTimers()
    if (this._unsubscribeUsdtRate) this._unsubscribeUsdtRate()
  },
  methods: {
    resubscribeUsdtRate () {
      if (this._unsubscribeUsdtRate) {
        this._unsubscribeUsdtRate()
        this._unsubscribeUsdtRate = null
      }
      this._unsubscribeUsdtRate = subscribeUsdtFiatRate(this.siteCurrency, (state) => {
        this.usdtRate = state.rate
      })
    },
    onUsdtQuoteChange (quote) {
      this.usdtQuote = {
        baseUsdt: quote.baseUsdt,
        totalUsdt: quote.totalUsdt
      }
    },
    formatMoney,
    formatUtcDateTime,
    displayValue,
    chargeStatusLabel,
    chargeStatusClass,
    chargeTypeLabel,
    chargeTypeClass,
    formatChargeProcDate,
    formatChargeAccountInfo (row) {
      if (!row) return '-'
      const tether = formatTetherAccountInfo(row.account)
      if (tether) return tether
      return this.displayValue(row.account)
    },
    formatChargeAccountInfoTitle (row) {
      if (!row) return ''
      const tether = formatTetherAccountInfoTitle(row.account)
      if (tether) return tether
      return String(row.account || '').trim()
    },
    syncDepositTabWithPaymentMethod () {
      this.depositTab = normalizeWalletTab(this.paymentMethod, this.depositTab)
    },
    setDepositTab (tab) {
      if (!canSelectWalletTab(this.paymentMethod, tab)) return
      if (this.depositTab === tab) return
      this.depositTab = tab
      this.submitError = ''
      this.bankSubmitError = ''
      if (tab === 'tether') {
        this.resetWalletCheck()
      }
    },
    resetWalletCheck () {
      this.walletCheckLoading = false
      this.walletCheckVerified = false
      this.walletUsdtBalance = null
      this.walletCheckError = ''
    },
    onWalletAddressInput () {
      this.resetWalletCheck()
      this.submitError = ''
    },
    async checkWalletBalance () {
      const address = this.resolveAccount()
      if (!address) {
        this.walletCheckError = this.$t('wallet.depositWalletRequired')
        return
      }
      if (!isValidTronAddress(address)) {
        this.walletCheckVerified = false
        this.walletUsdtBalance = null
        this.walletCheckError = this.$t('wallet.invalidTronAddress')
        return
      }

      this.walletCheckLoading = true
      this.walletCheckError = ''
      this.walletCheckVerified = false
      this.walletUsdtBalance = null
      try {
        const balance = await fetchTrc20UsdtBalance(address)
        this.walletUsdtBalance = balance
        this.walletCheckVerified = true
      } catch (err) {
        this.walletCheckVerified = false
        this.walletUsdtBalance = null
        this.walletCheckError = (err && err.message)
          ? err.message
          : this.$t('wallet.walletBalanceFail')
      } finally {
        this.walletCheckLoading = false
      }
    },
    prefillBankInputFromCookies () {
      if (!this.bankInputName) {
        this.bankInputName = String(getCookie('BankName') || '').trim()
      }
      if (!this.bankInputMaster) {
        this.bankInputMaster = String(getCookie('BankMaster') || '').trim()
      }
      if (!this.bankInputAccount) {
        this.bankInputAccount = String(getCookie('BankAccount') || '').trim()
      }
    },
    reload () {
      this.loadChargeHistory()
    },
    reloadHistory () {
      this.loadChargeHistory()
    },
    goHistoryPage (page) {
      if (page < 1 || page > this.historyTotalPages || page === this.historyPage) return
      this.historyPage = page
      this.loadChargeHistory()
    },
    async loadChargeHistory () {
      this.historyLoading = true
      this.historyError = ''
      try {
        const res = await WalletService.chargeList({
          offset: (this.historyPage - 1) * this.historyPageSize,
          limit: this.historyPageSize,
          loginid: getLoginId()
        })
        const body = res && res.data ? res.data : null
        if (body && body.code === 0 && body.data) {
          this.historyRows = normalizeChargeList(body.data)
          this.historyTotal = Number(body.data.total) || this.historyRows.length
        } else {
          this.historyRows = []
          this.historyTotal = 0
          this.historyError = (body && body.message)
            ? body.message
            : this.$t('wallet.loadDepositHistoryError')
        }
      } catch (err) {
        this.historyRows = []
        this.historyTotal = 0
        this.historyError = this.$t('wallet.loadDepositHistoryError')
      } finally {
        this.historyLoading = false
      }
    },
    onAmountInput () {
      const digits = String(this.amountDisplay || '').replace(/[^\d]/g, '')
      this.amount = digits ? Number(digits) : 0
      this.amountDisplay = digits ? formatIntegerInput(this.amount) : ''
    },
    addAmount (value) {
      this.amount = (Number(this.amount) || 0) + Number(value)
      this.amountDisplay = formatIntegerInput(this.amount)
    },
    resetAmount () {
      this.amount = 0
      this.amountDisplay = ''
    },
    resolveAccount () {
      return String(this.account || '').trim()
    },
    clearTetherSuccess () {
      this.successTetherAddress = ''
      this.tetherQrUrl = ''
      this.tetherQrLoading = false
      this.addressCopied = false
      if (this.addressCopiedTimer) {
        window.clearTimeout(this.addressCopiedTimer)
        this.addressCopiedTimer = null
      }
    },
    clearBankSuccess () {
      this.successBankDetails = {
        bankName: '',
        bankAccount: '',
        bankMaster: ''
      }
      this.bankSuccessMessage = ''
      this.bankCopyState = {
        bankName: false,
        accountNo: false,
        holder: false
      }
      this.clearBankCopyTimers()
    },
    clearBankCopyTimers () {
      Object.keys(this.bankCopyTimers).forEach((key) => {
        window.clearTimeout(this.bankCopyTimers[key])
      })
      this.bankCopyTimers = {}
    },
    async copyBankField (key, value) {
      const text = String(value || '').trim()
      if (!text) return
      const copied = await copyToClipboard(text)
      if (!copied) {
        showToast(this.$t('common.copyFail'))
        return
      }
      this.$set(this.bankCopyState, key, true)
      showToast(this.$t('common.copySuccess'))
      if (this.bankCopyTimers[key]) {
        window.clearTimeout(this.bankCopyTimers[key])
      }
      this.bankCopyTimers[key] = window.setTimeout(() => {
        this.$set(this.bankCopyState, key, false)
        delete this.bankCopyTimers[key]
      }, 2000)
    },
    async copyTetherAddress () {
      const address = String(this.successTetherAddress || '').trim()
      if (!address) return
      const copied = await copyToClipboard(address)
      if (copied) {
        this.addressCopied = true
        showToast(this.$t('wallet.tetherAddressCopied'))
        if (this.addressCopiedTimer) {
          window.clearTimeout(this.addressCopiedTimer)
        }
        this.addressCopiedTimer = window.setTimeout(() => {
          this.addressCopied = false
          this.addressCopiedTimer = null
        }, 2000)
      } else {
        showToast(this.$t('wallet.addressCopyFail'))
      }
    },
    async loadTetherQr () {
      const address = String(this.successTetherAddress || '').trim()
      if (!address) {
        this.tetherQrUrl = ''
        this.tetherQrLoading = false
        return
      }
      this.tetherQrLoading = true
      this.tetherQrUrl = ''
      try {
        this.tetherQrUrl = await generateQrDataUrl(address, 220)
      } catch (err) {
        this.tetherQrUrl = ''
      } finally {
        this.tetherQrLoading = false
      }
    },
    async submitDeposit () {
      this.submitError = ''
      this.successMessage = ''
      this.clearTetherSuccess()

      if (!this.amount || this.amount < this.minChargeAmount) {
        this.submitError = this.$t('wallet.depositMinAmount', { amount: this.formatCurrency(this.minChargeAmount) })
        return
      }

      if (!this.resolveAccount()) {
        this.submitError = this.$t('wallet.depositWalletRequired')
        return
      }

      if (!this.walletCheckVerified) {
        this.submitError = this.$t('wallet.depositWalletCheckRequired')
        return
      }

      const memo = buildChargeRequestMemo(this.amount, this.usdtRate)
      if (!memo) {
        this.submitError = this.$t('wallet.depositRateLoading')
        return
      }

      this.submitting = true
      try {
        const res = await WalletService.chargeRequest({
          amount: this.amount,
          account: buildTetherChargeAccount(this.resolveAccount()),
          memo
        })
        const body = res && res.data ? res.data : null
        if (body && body.code === 0) {
          const apiMessage = String(body.message || '').trim()
          this.successMessage = (apiMessage && apiMessage.indexOf('{') < 0)
            ? apiMessage
            : this.$t('wallet.depositSuccess')
          const tetherAddress = extractTetherFromChargeData(body.data)
          if (tetherAddress) {
            this.successTetherAddress = tetherAddress
            await this.loadTetherQr()
          }
          this.historyPage = 1
          this.loadChargeHistory()
          this.$emit('success')
        } else {
          const error = resolveChargeRequestError(body)
          this.submitError = this.$t(error.key, error.params)
        }
      } catch (err) {
        this.submitError = this.$t('wallet.depositFail')
      } finally {
        this.submitting = false
      }
    },
    selectPgWayCode (code) {
      if (this.pgWayCode === code) return
      this.pgWayCode = code
      this.pgAmount = 0
      this.pgSubmitError = ''
    },
    selectPgAmount (amount) {
      this.pgAmount = this.pgAmount === amount ? 0 : amount
      this.pgSubmitError = ''
    },
    syncPgWayCodeFromSite () {
      if (normalizePgDepositWayCode(this.pgWayCode)) return
      this.pgWayCode = resolveDefaultPgUserWayCode(this.siteInfo, { forTransfer: false })
    },
    syncPgAmountWithWayCode () {
      if (!this.pgAmount) return
      const allowed = getPgDepositAmounts(this.pgWayCode)
      if (!allowed.includes(this.pgAmount)) {
        this.pgAmount = 0
      }
    },
    async submitPgDeposit () {
      this.pgSubmitError = ''
      this.pgSuccessMessage = ''
      this.pgCashierUrl = ''

      if (!this.pgWayCode) {
        this.pgSubmitError = this.$t('wallet.pgWayCodeRequired')
        return
      }
      if (!this.pgAmount) {
        this.pgSubmitError = this.$t('wallet.pgAmountRequired')
        return
      }

      this.pgSubmitting = true
      try {
        const apiWayCode = toPgApiWayCode(this.pgWayCode)
        const res = await WalletService.chargeRequest({
          amount: this.pgAmount,
          account: `PG:${apiWayCode}`,
          memo: `PG deposit ${apiWayCode}`,
          way_code: apiWayCode,
          return_url: typeof window !== 'undefined' ? window.location.href : ''
        })
        const body = res && res.data ? res.data : null
        if (body && body.code === 0) {
          const data = body.data || {}
          const cashierUrl = data.cashier_url || data.bankinfo || ''
          this.pgSuccessMessage = this.$t('wallet.depositSuccess')
          this.pgCashierUrl = cashierUrl
          this.historyPage = 1
          this.loadChargeHistory()
          this.$emit('success')
          if (cashierUrl) {
            window.open(cashierUrl, '_blank', 'noopener,noreferrer')
          }
        } else {
          const error = resolveChargeRequestError(body)
          this.pgSubmitError = this.$t(error.key, error.params)
        }
      } catch (err) {
        this.pgSubmitError = this.$t('wallet.depositFail')
      } finally {
        this.pgSubmitting = false
      }
    },
    async submitBankDeposit () {
      this.bankSubmitError = ''
      this.bankSuccessMessage = ''
      this.clearBankSuccess()

      if (!this.amount || this.amount < this.minChargeAmount) {
        this.bankSubmitError = this.$t('wallet.depositMinAmount', { amount: this.formatCurrency(this.minChargeAmount) })
        return
      }

      const bankName = String(this.bankInputName || '').trim()
      const bankMaster = String(this.bankInputMaster || '').trim()
      const bankAccount = String(this.bankInputAccount || '').trim()

      if (!bankName) {
        this.bankSubmitError = this.$t('wallet.bankNameRequired')
        return
      }
      if (!bankMaster) {
        this.bankSubmitError = this.$t('wallet.depositorRequired')
        return
      }
      if (!bankAccount) {
        this.bankSubmitError = this.$t('wallet.accountRequired')
        return
      }

      this.bankSubmitting = true
      try {
        const res = await WalletService.chargeRequest({
          amount: this.amount,
          account: buildBankChargeAccount({
            bankMaster,
            bankName,
            bankAccount
          }),
          memo: ''
        })
        const body = res && res.data ? res.data : null
        if (body && body.code === 0) {
          const apiMessage = String(body.message || '').trim()
          this.bankSuccessMessage = (apiMessage && apiMessage.indexOf('{') < 0)
            ? apiMessage
            : this.$t('wallet.depositSuccess')
          if (body.data) {
            this.successBankDetails = extractBankFromChargeData(body.data)
          }
          this.historyPage = 1
          this.loadChargeHistory()
          this.$emit('success')
        } else {
          const error = resolveChargeRequestError(body)
          this.bankSubmitError = this.$t(error.key, error.params)
        }
      } catch (err) {
        this.bankSubmitError = this.$t('wallet.depositFail')
      } finally {
        this.bankSubmitting = false
      }
    }
  }
}
</script>
